import type { PriceQuote } from "../lib/api/types";
import { dollars, dollarsCents } from "../lib/format";

// Single source of truth for the size-appropriate headline (promo-aware 16×7 for doubles, plain
// 8×7 for singles) and the matching financing monthly. Shared with the mobile quote bar so the two
// price surfaces can never disagree. headlineCents <= 0 means "no resolvable price" — callers must
// show "Pricing on request", never "$0" (a rule may have priced only the other size).
export function priceSummary(price: PriceQuote, isSingle: boolean): { headlineCents: number; showPromo: boolean; monthlyCents: number | null } {
  const showPromo = !isSingle && price.promo16x7Cents != null;
  const primaryCents = isSingle ? price.price8x7Cents : price.price16x7Cents;
  return {
    headlineCents: showPromo ? price.promo16x7Cents! : primaryCents,
    showPromo,
    monthlyCents: (isSingle ? price.financeMonthly8x7Cents : price.financeMonthly16x7Cents) ?? null,
  };
}

// Single source of truth for how a priced door reads to the customer:
//  - always prefixed "Starting from" (every price is an estimate the dealer can upsell)
//  - shows the size-appropriate headline only (8×7 for a single, 16×7 for a double) — never the
//    opposite-size figure
//  - promo strikethrough + label when a 16×7 promo is active (doubles only)
//  - optional "· $X/mo" + financing terms when the tenant has financing enabled
// Used by the editor price card (VisualizePage) and each door card on the get-quote page.
export function PriceLine({
  price,
  isSingle,
  financingEnabled = false,
  financingTermMonths,
  financingApr,
  title,
  subtext,
}: {
  price: PriceQuote;
  isSingle: boolean;
  financingEnabled?: boolean;
  financingTermMonths?: number;
  financingApr?: number;
  title?: string;
  subtext?: string;
}) {
  // A single shows the 8×7 column (no promo); a double the promo-aware 16×7 column.
  const { headlineCents, showPromo, monthlyCents } = priceSummary(price, isSingle);
  const showMonthly = financingEnabled && monthlyCents != null;

  // A rule that priced only the other size leaves this size at 0 — never render "$0". Show the same
  // "Pricing on request" the SchedulePage uses when no quote resolves.
  if (headlineCents <= 0) {
    return (
      <div>
        {title && <div style={{ fontSize: 12, color: "var(--muted)", marginBottom: 2 }}>{title}</div>}
        <div style={{ fontSize: 13, fontWeight: 600, color: "var(--muted)" }}>Starting from</div>
        <div style={{ fontSize: 15, fontWeight: 600, color: "var(--muted)", marginTop: 2 }}>Pricing on request</div>
        {subtext && <div style={{ fontSize: 12, color: "var(--muted)", marginTop: 3 }}>{subtext}</div>}
      </div>
    );
  }

  return (
    <div>
      {title && (
        <div style={{ fontSize: 12, color: "var(--muted)", marginBottom: 2 }}>{title}</div>
      )}
      <div style={{ display: "flex", alignItems: "baseline", gap: 8, flexWrap: "wrap" }}>
        <span style={{ fontSize: 13, fontWeight: 600, color: "var(--muted)" }}>Starting from</span>
        {showPromo ? (
          <>
            <span style={{ fontSize: 22, fontWeight: 700, color: "var(--text)" }}>{dollars(price.promo16x7Cents!)}</span>
            <span style={{ fontSize: 15, color: "var(--muted)", textDecoration: "line-through" }}>{dollars(price.price16x7Cents)}</span>
            {price.promoLabel && (
              <span style={{ fontSize: 11, fontWeight: 600, color: "var(--brand)", background: "var(--brand-tint)", borderRadius: "var(--radius-pill)", padding: "2px 8px" }}>{price.promoLabel}</span>
            )}
          </>
        ) : (
          <span style={{ fontSize: 22, fontWeight: 700, color: "var(--text)" }}>{dollars(headlineCents)}</span>
        )}
        {showMonthly && (
          <span style={{ fontSize: 14, fontWeight: 600, color: "var(--muted)" }}>· {dollarsCents(monthlyCents!)}/mo</span>
        )}
      </div>
      {subtext && <div style={{ fontSize: 12, color: "var(--muted)", marginTop: 3 }}>{subtext}</div>}
      {showMonthly && financingTermMonths != null && financingApr != null && (
        <div style={{ fontSize: 11, color: "var(--muted)", marginTop: 4 }}>
          {financingTermMonths} mo @ {financingApr}% APR
        </div>
      )}
    </div>
  );
}
